import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import * as Sentry from "@sentry/react";

import { graphqlMainService, graphqlRealtimeService } from "~/config/appConfig";

import { getEnvironment } from "~/utils/environment";
import { limitDepth } from "~/utils/objects/objectHelpers";
import { composeUrl } from "~/utils/url/composeUrl";

const environment = getEnvironment();

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const DEFAULT_TRACES_SAMPLE_RATE = 0.01;
const SENTRY_DSN = environment.NEXT_PUBLIC_SENTRY_DSN;
const ENVIRONMENT =
  (environment.DEV && "local") || environment.NEXT_PUBLIC_SENTRY_ENVIRONMENT;
const configuredTracesSampleRate = Number(
  environment.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE
);
const tracesSampleRate =
  Number.isFinite(configuredTracesSampleRate) &&
  configuredTracesSampleRate >= 0 &&
  configuredTracesSampleRate <= 1
    ? configuredTracesSampleRate
    : DEFAULT_TRACES_SAMPLE_RATE;
const GSI_FEDCM_ABORT_ERROR =
  /^\[GSI_LOGGER\]: FedCM get\(\) rejects with AbortError: signal is aborted without reason$/;
const GSI_FEDCM_TOKEN_NETWORK_ERROR =
  /^\[GSI_LOGGER\]: FedCM get\(\) rejects with NetworkError: Error retrieving a token\.$/;

Sentry.init({
  // Do not initialize transport in local development or without a GlitchTip DSN.
  enabled: Boolean(SENTRY_DSN) && ENVIRONMENT !== "local",

  // Limit the depth of the breadcrumbs to ensure that errors are not rejected by Sentry.
  beforeBreadcrumb(breadcrumb) {
    return limitDepth(breadcrumb, 1);
  },

  dsn: SENTRY_DSN,

  // Google Identity Services logs these browser-level FedCM failures to the
  // console. They are not application exceptions and should not create issues.
  ignoreErrors: [GSI_FEDCM_ABORT_ERROR, GSI_FEDCM_TOKEN_NETWORK_ERROR],

  // A number between 0 and 1, controlling the percentage chance a given transaction will be sent to Sentry.
  tracesSampleRate,

  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  tracePropagationTargets: [
    /^\//,
    composeUrl({
      hostname: graphqlMainService.hostname,
      protocol: graphqlMainService.protocolHttp,
      port: graphqlMainService.port,
      apiPrefix: graphqlMainService.apiPrefix,
    }),
    composeUrl({
      hostname: graphqlRealtimeService.hostname,
      protocol: graphqlRealtimeService.protocolHttp,
      port: graphqlRealtimeService.port,
      apiPrefix: graphqlRealtimeService.apiPrefix,
    }),
  ],

  /* By default Sentry SDKs normalize any context to a depth of 3,
   which in the case of sending Redux state you probably will want to increase that. */
  normalizeDepth: 7,

  // debug: ENVIRONMENT === "local",

  // ...
  // Note: if you want to override the automatic release value, do not set a
  // `release` value here - use the environment variable `SENTRY_RELEASE`, so
  // that it will also get attached to your source maps
  release: `${environment.NEXT_PUBLIC_APP_NAME}@${environment.NEXT_PUBLIC_APP_VERSION}`,

  // Tag the release with the environment
  environment: ENVIRONMENT,

  integrations(defaultIntegrations) {
    return [
      // GlitchTip does not support Sentry's session tracking protocol.
      ...defaultIntegrations.filter(
        (integration) => integration.name !== "BrowserSession"
      ),
      Sentry.captureConsoleIntegration({
        levels: ["error"],
      }),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ];
  },
});
